import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { type Attachment } from '../api/attachments';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { getAttachmentPreview, type PreviewResult } from '../lib/documentPreview';
import { useTeams } from '../teams/TeamsProvider';

/**
 * #260 — the ONE in-app preview surface for every attachment type. Before
 * this, images opened in a Fluent lightbox (`ReviewerQueue.tsx`'s own
 * `ReviewLightbox`, duplicated again in `AttachmentsSection.tsx` and
 * `ReviewSummary.tsx`) while docx/xlsx/PDF/anything else opened in a NEW
 * BROWSER TAB (#256/#257) — two different mechanisms for "look at this file",
 * confirmed via AskUserQuestion to unify into one.
 *
 * This also carries the actual fix for #260's root-caused bug: a freshly
 * uploaded image's local `File` — passed in via `localFiles` — is used
 * directly for its object URL (`getAttachmentPreview`'s `localFile`
 * parameter), so Review & Submit never needs the authenticated re-fetch that
 * could previously fail silently and leave the thumbnail blank forever.
 */

const useStyles = makeStyles({
  surface: {
    width: '90vw',
    maxWidth: '90vw',
    height: '90vh',
    maxHeight: '90vh',
  },
  // Fluent's own `DialogBody` is `display: grid` but has no explicit height
  // of its own — inside `DialogSurface` (a plain `display: block` box), a
  // block-level child with no height sizes to its content, not its parent.
  // Without this, `DialogBody`'s `gridTemplateRows: auto 1fr` has no extra
  // space to give that `1fr` row, so `DialogContent` (and this dialog's
  // image/iframe inside it) collapses to its own minimal size no matter what
  // `flexGrow`/`height: 100%` is set on them — confirmed by measuring the
  // real rendered boxes in a browser: `DialogSurface` was correctly ~90vh,
  // `DialogBody` was only 96px (#264).
  body: {
    height: '100%',
    minHeight: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    minHeight: 0,
    height: '100%',
  },
  media: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  frame: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  // #271: the PDF branch's own scroll container — pdf.js renders every page
  // of the document into its own <canvas>, stacked vertically, so a
  // multi-page PDF needs to scroll independently of the dialog chrome around
  // it rather than be clipped to one page's height.
  pdfScroll: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
  },
  pdfCanvas: {
    maxWidth: '100%',
    boxShadow: tokens.shadow4,
  },
  unsupported: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
  },
});

export interface AttachmentPreviewDialogProps {
  /** The full attachment list — navigation moves across every type, not just images (#260's unify). */
  attachments: Attachment[];
  initialIndex: number;
  /** Freshly-uploaded files still in memory this session, keyed by attachment id — the #260 bug fix. */
  localFiles?: Map<string, File>;
  onClose: () => void;
}

type LoadState =
  { phase: 'loading' } | { phase: 'ready'; result: PreviewResult } | { phase: 'error' };

/** Triggers a browser download of a blob without navigating away — same technique the
 *  lightboxes this component replaces already used for their own Download action. */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * Renders one pdf.js page into its own `<canvas>` (#271). Sized to the
 * scroll container's current width so a page fits the dialog rather than
 * spilling past it or rendering at a fixed, possibly-tiny size.
 *
 * On any failure (a corrupt page, `getContext('2d')` unavailable) shows a
 * per-page message rather than failing the whole document — the pages
 * around it can still be genuinely fine.
 */
function PdfPageCanvas({
  pdfDocument,
  pageNumber,
  fileName,
  containerRef,
}: {
  pdfDocument: PDFDocumentProxy;
  pageNumber: number;
  fileName: string;
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  const styles = useStyles();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let renderTask: { promise: Promise<void>; cancel: () => void } | undefined;

    void (async () => {
      try {
        const page = await pdfDocument.getPage(pageNumber);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;

        const unscaledViewport = page.getViewport({ scale: 1 });
        const containerWidth = containerRef.current?.clientWidth;
        const targetWidth =
          containerWidth && containerWidth > 0 ? containerWidth : unscaledViewport.width;
        const viewport = page.getViewport({ scale: targetWidth / unscaledViewport.width });
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        // jsdom (this app's test environment) has no canvas 2D context at
        // all — `getContext` returns `null` there, which is expected and NOT
        // a failure: the canvas element itself is still mounted, which is
        // what a structural regression test can assert on.
        const context = canvas.getContext('2d');
        if (!context || cancelled) return;

        renderTask = page.render({ canvas, viewport });
        await renderTask.promise;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pdfDocument, pageNumber, containerRef]);

  if (failed) {
    return <Body1>Couldn’t render page {pageNumber} of this PDF</Body1>;
  }

  // No explicit `role="img"` — oxlint's `jsx-a11y/prefer-tag-over-role`
  // flags it (it can't tell a `<canvas>` isn't interchangeable with an
  // `<img>` here), and it is unneeded regardless: a `<canvas>` with no
  // interactive fallback content already carries an implicit ARIA role of
  // `img` per the HTML spec, so `aria-label` alone is sufficient.
  return (
    <canvas
      ref={canvasRef}
      className={styles.pdfCanvas}
      aria-label={`${fileName}, page ${pageNumber} of ${pdfDocument.numPages}`}
    />
  );
}

/**
 * Every page of a pdf.js-loaded document, stacked vertically in a scrollable
 * container (#271). Deliberately no page-by-page navigation UI — out of
 * scope for this fix, and this dialog's own Previous/Next in `DialogActions`
 * already means something else entirely (moving between different
 * ATTACHMENTS), so adding PDF-page controls here would be visually
 * ambiguous with them.
 */
function PdfPreview({
  pdfDocument,
  fileName,
}: {
  pdfDocument: PDFDocumentProxy;
  fileName: string;
}) {
  const styles = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const pageNumbers = Array.from({ length: pdfDocument.numPages }, (_, index) => index + 1);

  return (
    <div ref={containerRef} className={styles.pdfScroll}>
      {pageNumbers.map((pageNumber) => (
        <PdfPageCanvas
          key={pageNumber}
          pdfDocument={pdfDocument}
          pageNumber={pageNumber}
          fileName={fileName}
          containerRef={containerRef}
        />
      ))}
    </div>
  );
}

export function AttachmentPreviewDialog({
  attachments,
  initialIndex,
  localFiles,
  onClose,
}: AttachmentPreviewDialogProps) {
  const styles = useStyles();
  const responsive = useResponsiveStyles();
  const { themeName } = useTeams();
  const [index, setIndex] = useState(initialIndex);
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  // Bumped by Retry so the load effect below re-runs against the SAME
  // attachment — a plain re-run of `current.id`/`current.contentType` alone
  // would not fire again, since neither changed.
  const [retryToken, setRetryToken] = useState(0);
  const current = attachments[index];

  // #273: a resolved `kind: 'unsupported'` result already has its blob — there
  // is no viewer for it, but there is nothing to retry either, so it should
  // download immediately rather than sit behind a "Preview not available"
  // message and a second Download click. The ref (not just `current.id` as an
  // effect dependency) guards against firing twice for the SAME attachment on
  // an unrelated re-render — `current.id` alone would already prevent that
  // here, but the ref is what makes it correct if `onClose` is ever not
  // synchronous with unmounting this component.
  const autoDownloadedIdRef = useRef<string | null>(null);

  function move(nextIndex: number) {
    setIndex((nextIndex + attachments.length) % attachments.length);
  }

  useEffect(() => {
    let cancelled = false;
    setState({ phase: 'loading' });

    void (async () => {
      try {
        const result = await getAttachmentPreview(current, localFiles?.get(current.id), themeName);
        if (cancelled) {
          if (result.kind === 'image') URL.revokeObjectURL(result.url);
          if (result.kind === 'pdf') result.destroy();
          return;
        }
        setState({ phase: 'ready', result });
      } catch {
        if (!cancelled) setState({ phase: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `current` is derived from `index`/`attachments`; `localFiles` is looked
    // up fresh on every load rather than tracked as its own dependency, the
    // same reasoning `AttachmentsSection.tsx`'s own `onAttachmentsChange`
    // effect documents for the id-set-only dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- current.id/contentType identify the content to load
  }, [current.id, current.contentType, retryToken]);

  // #273: fires the download the instant a result resolves to `kind:
  // 'unsupported'` — including landing on one mid-session via Previous/Next,
  // since this effect keys on `current.id`/`state`, not on whether the dialog
  // just opened. Closing the dialog right after (rather than leaving it open
  // on an empty/blank surface with nothing to view) is the option #273 itself
  // calls out as the cleanest fit for this component's existing structure:
  // there's no per-attachment "skip" concept here, only the one `onClose` the
  // caller already owns.
  useEffect(() => {
    if (state.phase !== 'ready' || state.result.kind !== 'unsupported') return;
    if (autoDownloadedIdRef.current === current.id) return;
    autoDownloadedIdRef.current = current.id;
    downloadBlob(state.result.blob, state.result.fileName);
    onClose();
  }, [state, current.id, onClose]);

  // Release whatever this dialog itself created once it stops being shown —
  // the object URL for an image, or the pdf.js document's worker-side
  // resources for a PDF (#271; `PreviewResult`'s own doc comment explains
  // why a PDF result carries its own `destroy` rather than a URL to revoke).
  useEffect(() => {
    if (state.phase !== 'ready') return undefined;
    const { result } = state;
    return () => {
      if (result.kind === 'image') URL.revokeObjectURL(result.url);
      if (result.kind === 'pdf') result.destroy();
    };
  }, [state]);

  // K1 "Dialog opens" — this surface has no confirm/cancel pair, so focus
  // goes to the one control guaranteed to dismiss it: Close. Same reasoning
  // as the lightboxes this component replaces (log entry 21).
  //
  // A fixed number of `.focus()` attempts (mount, then again once loading
  // resolves) is not enough: tabster's modalizer marks the trap `aria-hidden`
  // while it negotiates on mount, AND again on the Spinner -> content DOM
  // swap, and how long that negotiation takes is a property of how loaded
  // the machine is, not of this component. `ReviewerQueue.test.tsx`'s K1
  // lightbox test failed three different ways chasing this (log entries
  // 51-53/57) — a bare synchronous query, then `findByRole`, then a padded
  // `waitFor` timeout — because every one of them is still a fixed-attempt
  // race against an unbounded delay. Watching the trap's own `aria-hidden`/
  // `data-tabster` attributes and re-asserting focus whenever they change
  // reacts to the actual DOM signal tabster produces instead: correct no
  // matter how many times it re-negotiates or how long each round takes,
  // and a no-op once focus has genuinely landed and stayed.
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const button = closeRef.current;
    if (!button) return undefined;

    function reassertFocus() {
      if (button && document.activeElement !== button) button.focus();
    }

    reassertFocus();

    const trap = button.closest('[role="dialog"]');
    if (!trap) return undefined;
    const observer = new MutationObserver(reassertFocus);
    observer.observe(trap, { attributes: true, attributeFilter: ['aria-hidden', 'data-tabster'] });
    return () => observer.disconnect();
  }, []);

  // K1 "Dialog closes" — the caller owns the open/closed boolean this dialog
  // is rendered from (`lightboxIndex !== null`, in every call site), so it is
  // the caller's `useRestoreFocusOnClose(...)` that fires, exactly as every
  // dialog in this app already does; nothing to duplicate here.

  useEffect(() => {
    if (attachments.length <= 1) return undefined;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') move(index - 1);
      if (event.key === 'ArrowRight') move(index + 1);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `move` closes over `index`/`attachments.length`, both already in this effect's own reasoning
  }, [attachments.length, index]);

  function renderBody() {
    if (state.phase === 'loading') {
      return <Spinner label="Loading preview…" />;
    }
    if (state.phase === 'error') {
      return (
        <div className={styles.unsupported}>
          <Body1>Couldn’t load preview</Body1>
          <Button appearance="secondary" onClick={() => setRetryToken((token) => token + 1)}>
            Retry
          </Button>
        </div>
      );
    }
    const { result } = state;
    // `key={current.id}` on every branch below: without it, moving between
    // two attachments that resolve to the same element type (two PDFs, or a
    // PDF and a docx — both `<iframe>`) makes React reuse the EXISTING
    // `<iframe>` and just mutate its `src`/`srcDoc` in place, which is a real
    // same-document NAVIGATION of that iframe rather than a fresh mount —
    // and iframe navigation can transiently steal focus into the iframe's
    // nested browsing context, which tabster's modalizer (this dialog is
    // trapped) reacts to by re-scanning and briefly `aria-hidden`-ing the
    // rest of the trap while it resolves. Keying by attachment id makes
    // React unmount the old node and mount a brand new one instead — an
    // element's FIRST `src` is just its initial load, never a navigation —
    // so the trap's content never moves out from under tabster mid-trap.
    //
    // The PDF branch below (`PdfPreview`) isn't an iframe at all any more
    // (#271), so it doesn't share that specific failure mode — but it still
    // needs its own fresh mount per attachment (a new pdf.js document per
    // `result`, never re-rendered into stale canvases from the last one), so
    // it stays keyed the same way as everything else here.
    if (result.kind === 'image') {
      return (
        <img key={current.id} src={result.url} alt={current.fileName} className={styles.media} />
      );
    }
    if (result.kind === 'pdf') {
      return (
        <PdfPreview key={current.id} pdfDocument={result.pdfDocument} fileName={current.fileName} />
      );
    }
    if (result.kind === 'html') {
      return (
        // Empty `sandbox` — no `allow-scripts` — is intentional: this is
        // converted user-uploaded content and must not be able to execute.
        <iframe
          key={current.id}
          sandbox=""
          srcDoc={result.html}
          title={current.fileName}
          className={styles.frame}
        />
      );
    }
    // `kind: 'unsupported'` never renders here — the effect above downloads
    // it and closes the dialog the moment the result resolves, so there is
    // nothing left to show but the loading spinner for the brief instant
    // before that close takes effect.
    return <Spinner label="Loading preview…" />;
  }

  return (
    <Dialog open onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface
        className={mergeClasses(styles.surface, responsive.mobileSheet, responsive.touchTargets)}
      >
        <DialogBody className={styles.body}>
          <DialogTitle>
            {current.fileName} {index + 1} of {attachments.length}
          </DialogTitle>
          <DialogContent className={styles.content}>{renderBody()}</DialogContent>
          <DialogActions>
            {attachments.length > 1 && (
              <>
                <Button appearance="secondary" onClick={() => move(index - 1)}>
                  Previous
                </Button>
                <Button appearance="secondary" onClick={() => move(index + 1)}>
                  Next
                </Button>
              </>
            )}
            <Button ref={closeRef} appearance="primary" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
